import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { Fragment, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { DocumentsData } from '@/features/ui/DocumentsData';

import { RMPServiceCode } from '@/shared/types/serviceCodes';
import {
  FormDatePicker,
  FormTextInput,
  FormSelect,
  FormSwitch,
  Button,
  StepControlPanel,
  ConditionView,
  ButtonVariant,
  FormTextArea,
  Grid,
  UKRAINE_PHONE_MASK_START,
} from '@/shared/ui';
import { MultipleFormInputPhone } from '@/shared/ui/InputPhone/MultipleFormInputPhone';

import { FormConstants } from '@/components/Forms/constants';

import { useERZDocumentTypes } from '@/modules/erz/helpers';

import { formatDate } from '@/helpers/dates';

import { rmp } from '@/api/integrations/rmp';
import { queryKeys } from '@/api/queryKeys';
import { DocumentTypeCode } from '@/api/types';

import { DnaMethodOptions, missingPersonAllowedFileTypes } from '../../lib/constants';
import { MissingPersonFormData } from '../../model/types';

import type { SelectOption } from '@/shared/types';

const MAX_DNA_OWNERS = 4;
const UNIQ_ANCESTRAL_CONNECTION_SELECT_ID = ['3', '4'];

export const ERDRInformation: React.FC<StepProps> = ({
  next,
  prev,
  serviceCode,
  currentRequestId,
  onStepDraftSave,
}) => {
  const { control, watch, formState, getValues, setValue } = useFormContext<MissingPersonFormData>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'erdr.dna_owners',
  });

  const dnaExists = watch('erdr.dna_exists');

  const { data: erdrDocs = [] } = useERZDocumentTypes({
    allowedDocumentTypeCodes: [DocumentTypeCode.EXTRACT_ERDR],
  });
  const extractErdrDocType = erdrDocs[0];

  const { data: ancestralOptions = [] } = useQuery({
    queryKey: queryKeys.rmp.familyRelationshipDegrees(serviceCode as string),
    queryFn: () =>
      rmp.dictionary.getFamilyRelationshipDegrees(serviceCode as RMPServiceCode.MISSING_PERSON).then((r) =>
        r.data.map((d) => ({
          id: d.id,
          label: d.name,
          required_documents: d.required_documents.map((rd) => ({ id: rd.id, label: rd.name })),
        })),
      ),
    enabled: !!serviceCode,
  });

  useEffect(() => {
    if (!dnaExists && fields.length > 0) {
      remove([...Array(fields.length).keys()]);
    }
    if (dnaExists && fields.length === 0) {
      addDNAOwner();
    }
  }, [dnaExists]);

  const addDNAOwner = (): void => {
    if (fields.length < MAX_DNA_OWNERS) {
      append({
        phones: undefined,
        method: null,
        lastName: '',
        name: '',
        patronymic: '',
        birthday: '',
        ancestralConnection: null,
      });
    }
  };

  const getAvailableAncestralOptions = (currentIndex: number): SelectOption[] => {
    const selectedIds = fields
      .filter((_, idx) => idx !== currentIndex)
      .map((f) => f.ancestralConnection?.id)
      .filter((id) => id && UNIQ_ANCESTRAL_CONNECTION_SELECT_ID.includes(id));
    return ancestralOptions.filter(
      (opt) =>
        !selectedIds.includes(opt.id as string) || !UNIQ_ANCESTRAL_CONNECTION_SELECT_ID.includes(opt.id as string),
    );
  };

  return (
    <>
      <FormBlock title="Номер кримінального провадження (ЄРДР)">
        <FormTextInput
          maxLength={20}
          name="erdr.number"
          control={control}
          label="Номер кримінального провадження (ЄРДР)"
        />
        <DocumentsData
          withUploader
          documentTypeSelectHidden
          fileRequired={false}
          maxDocumentsCount={1}
          maxFileCount={5}
          name="erdr.document_scanned_extract"
          documentUploadLabel="Сканкопія витягу з ЄРДР"
          request_id={currentRequestId ?? ''}
          documentTypeEntities={extractErdrDocType ? [extractErdrDocType] : []}
          onSaveDraft={onStepDraftSave}
          allowedFileTypes={missingPersonAllowedFileTypes}
        />
        <FormSwitch name="erdr.dna_exists" control={control} label="Наявність ДНК зразків" />
      </FormBlock>

      <ConditionView if={dnaExists}>
        <FormBlock title="ДНК зразки">
          {fields.map((field, index) => (
            <Fragment key={field.id}>
              <FormSelect
                name={`erdr.dna_owners.${index}.method`}
                control={control}
                label="У який спосіб відібрані ДНК зразки"
                required
                options={DnaMethodOptions}
              />
              <FormTextInput
                name={`erdr.dna_owners.${index}.lastName`}
                control={control}
                label="Прізвище особи, яка здала ДНК зразки"
                required
                maxLength={FormConstants.INPUT_MAX_LENGTH_240}
              />
              <FormTextInput
                name={`erdr.dna_owners.${index}.name`}
                control={control}
                label="Ім’я особи, яка здала ДНК зразки"
                required
                maxLength={FormConstants.INPUT_MAX_LENGTH_240}
              />
              <FormTextInput
                name={`erdr.dna_owners.${index}.patronymic`}
                control={control}
                label="По батькові особи, яка здала ДНК зразки"
                maxLength={FormConstants.INPUT_MAX_LENGTH_240}
              />
              <FormDatePicker
                name={`erdr.dna_owners.${index}.birthday`}
                control={control}
                label="Дата народження особи, яка здала ДНК зразки"
                required
                maxDate={dayjs()}
              />
              <FormSelect
                name={`erdr.dna_owners.${index}.ancestralConnection`}
                control={control}
                label="Ступінь родинного зв’язку із зниклим, особи, яка здала ДНК зразки"
                required
                options={getAvailableAncestralOptions(index)}
              />

              <MultipleFormInputPhone
                label="Контактний телефон особи, яка здала ДНК зразки"
                onChange={(newPhones) => newPhones && setValue(`erdr.dna_owners.${index}.phones`, newPhones)}
                value={getValues('erdr.dna_owners')[index].phones}
                onlyCountries={[]}
                errors={formState.errors.erdr?.dna_owners?.[index]?.phones as Array<{ message: string }>}
                maxPhoneNumbersQuantity={4}
              />

              {index > 0 && (
                <Button
                  type="button"
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => remove(index)}
                  style={{ marginTop: '8px' }}
                >
                  Видалити власника
                </Button>
              )}
            </Fragment>
          ))}
          {fields.length < MAX_DNA_OWNERS && (
            <Button type="button" variant={ButtonVariant.PRIMARY} onClick={addDNAOwner}>
              Додати ще одну особу, яка здала ДНК зразки
            </Button>
          )}
        </FormBlock>
      </ConditionView>
      <FormBlock title="Додаткова інформація">
        <FormTextArea
          name="erdr.additional_info"
          control={control}
          label="Якщо бажаєте, надайте додаткову інформацію до приведеної вище, що сприятиме пошуку особи зниклої безвісти за особливих обставин"
          rows={3}
          maxLength={FormConstants.INPUT_MAX_LENGTH_250}
        />
      </FormBlock>

      <StepControlPanel prev={prev} next={next} />
    </>
  );
};

export const ERDRInformationPreview: React.FC = () => {
  const { getValues } = useFormContext<MissingPersonFormData>();
  const erdr = getValues('erdr');

  return (
    <>
      <Grid
        title="Відомості про кримінальне провадження"
        data={[
          ['Номер кримінального провадження (ЄРДР)', erdr?.number],
          [
            'Сканкопія витягу з ЄРДР',
            erdr?.document_scanned_extract?.[0]?.attachments?.fileNames
              ? Object.values(erdr?.document_scanned_extract?.[0]?.attachments?.fileNames).join(', ')
              : (erdr?.document_scanned_extract?.[0]?.attachments?.file_name ??
                erdr?.document_scanned_extract?.[0]?.attachments?.fileName ??
                null),
          ],
          ['Чи відібрані ДНК зразки', erdr?.dna_exists ? 'Так' : 'Ні'],
          ['Додаткова інформація', erdr?.additional_info],
        ]}
      />

      {erdr?.dna_exists &&
        erdr?.dna_owners &&
        erdr?.dna_owners?.length > 0 &&
        erdr?.dna_owners?.map((owner, index) => (
          <Grid
            key={`${owner.name}_${owner.lastName}`}
            title={`ДНК зразки #${index + 1}`}
            data={[
              ['У який спосіб відібрані ДНК зразки', owner.method?.label ?? null],
              ['Прізвище особи, яка здала ДНК зразки', owner.lastName || null],
              ['Ім’я особи, яка здала ДНК зразки', owner.name || null],
              ['По батькові особи, яка здала ДНК зразки', owner.patronymic || null],
              ['Дата народження особи, яка здала ДНК зразки', owner.birthday ? formatDate(owner.birthday) : null],
              [
                'Ступінь родинного зв’язку із зниклим, особи, яка здала ДНК зразки',
                owner.ancestralConnection?.label ?? null,
              ],
              [
                'Контактний телефон особи, яка здала ДНК зразки',
                owner.phones && owner.phones.length > 0
                  ? owner.phones
                      ?.filter((phone) => phone !== UKRAINE_PHONE_MASK_START)
                      ?.map((phone) => phone)
                      ?.join(', ')
                  : null,
              ],
            ]}
          />
        ))}
      <Grid
        title="Додаткова інформація"
        data={[
          [
            'Якщо бажаєте, надайте додаткову інформацію до приведеної вище, що сприятиме пошуку особи зниклої безвісти за особливих обставин',
            erdr?.additional_info,
          ],
        ]}
      />
    </>
  );
};
